import { useState } from "react";

interface IncidentPayload {
  type: string;
  message?: string;
  timestamp: string;
}

type ReportStatus = "idle" | "sending" | "sent" | "error";

/**
 * Sends a user-triggered incident report to support@adjudon.com via the backend.
 * Falls back silently if the API is unreachable (ironic for an error component).
 */
export function useIncidentReport() {
  const [status, setStatus] = useState<ReportStatus>("idle");

  const report = async (payload: IncidentPayload) => {
    if (status === "sending" || status === "sent") return;
    setStatus("sending");

    const userEmail = (() => {
      try {
        const raw = localStorage.getItem("adjudon_user");
        if (raw) return JSON.parse(raw)?.email ?? undefined;
      } catch {
        return undefined;
      }
    })();

    try {
      const { ENV } = await import('../../config/env');
      const apiBase = ENV.API_BASE_URL;
      const url = apiBase ? `${apiBase}/api/support/incident-report` : '/api/support/incident-report';
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          url: window.location.href,
          userAgent: navigator.userAgent,
          userEmail,
        }),
      });
      setStatus("sent");
    } catch {
      // Don't surface a second error inside an error state — fail silently
      setStatus("error");
    }
  };

  return { report, status };
}
